import React from 'react';
import PropTypes from 'prop-types';
import Placeholder, { timedLoading } from 'components/Placeholder';
import classnames from 'classnames';
import { stringParams } from '../../utils';

const reportAttrs = [
  {
    key: 'inquiry',
    label: 'Inquiry',
    percentLabel: '-'
  },
  {
    key: 'applicant',
    label: 'Application',
    percentLabel: 'Conversion'
  },
  {
    key: 'application_complete',
    label: 'App. Complete',
    percentLabel: 'Conversion'
  },
  {
    key: 'accepted',
    label: 'Accepted',
    percentLabel: 'Selectivity'
  },
  {
    key: 'enrolled_new',
    label: 'Newly Enrolled',
    percentLabel: 'Yield'
  },
  {
    key: 'enrolled_returning',
    label: 'Returning',
    percentLabel: 'Retention'
  },
  {
    key: 'withdrawal',
    label: 'Withdrawal',
    percentLabel: '-'
  },
  {
    key: 'enrollment_in_progress',
    label: 'Enrollment In Progress',
    percentLabel: '_'
  },
  {
    key: 'enrolled',
    label: 'Total Enrolled',
    percentLabel: '-'
  }
];

const formatCount = (value) => {
  if (value === null) {
    return '-';
  }
  return value;
};

const formatPercent = (value) => {
  if (value === null) {
    return '-';
  }
  return `${value}%`;
};

const FunnelReportTable = (props) => {
  const {
    filters,
    asOfDate,
    funnelReport,
    reportType,
  } = props;

  const reportYears = [...filters.report_year].sort().reverse();

  const aggregateCounts = (dataKey, reportYear) => funnelReport
    .map(school => school.data[dataKey][reportYear].count || 0)
    .reduce((acc, val) => acc + val);

  const aggregatePercents = (dataKey, reportYear) => {
    // get report values with null values removed
    const reportValues = funnelReport
      .map((school => school.data[dataKey][reportYear].percent))
      .filter((a) => a !== null);

    if (reportValues.length === 0) {
      return '-';
    }

    const sum = reportValues.reduce((acc, val) => acc + val);
    return `${Math.round(sum / reportValues.length)}%`;
  };

  // calls `func` with useful params for each report attribute and report year
  const mapDataColumn = (func) => reportAttrs.map(
    (attr) => reportYears.map(
      (reportYear, i) => func({
        attr,
        reportYear,
        key: `${attr.key}-${reportYear}`,
        className: `col_${attr.key} ${i === 0 ? 'current_year' : 'prior_year'}`,
        isClickable: (school) => reportType === 'count' && school.data[attr.key][reportYear].count && !asOfDate,
      })
    )
  );

  const loadingMessage = (elapsed) =>
    (elapsed > 10)
      ? "Loading... This report is taking a little longer to load due to the amount of data being processed. Please be patient - your data will be ready soon!"
      : "Loading...";

  const onSchoolClick = (school, status, year) => {
    const baseUrl = props.customer.base_url.replace('https:', 'http:')
    if (status === 'admin') {
      const tempUrl = baseUrl.replace(props.customer.subdomain, school.subdomain) + status
      window.open(tempUrl)
    }
    else {
      const tempUrl = baseUrl.replace(props.customer.subdomain, school.subdomain) + `api/reporting/combined_funnel_report/search_redirect?` +
        stringParams({status: status, start_year: year, filter: filters})
      window.open(tempUrl)
    }
  }

  return (
    <Placeholder.loading
      ready={funnelReport.ready}
      loading={timedLoading(loadingMessage)}
      content={() => (
        <table className='table combined-funnel-report'>
          <thead>
            <tr>
              <th />
              {reportAttrs.map(attr => (
                <th
                  key={attr.key}
                  className={`col_${attr.key} main_header`}
                  colSpan={reportYears.length}
                  title={(reportType === 'percentage' && attr.percentLabel !== '-') ? attr.percentLabel : ''}
                >
                  {attr.label}
                </th>
              ))}
              <th className='col_target main_header' colSpan={reportYears.length}>Target</th>
            </tr>
            <tr>
              <th />

              {mapDataColumn(({ className, key, reportYear }) =>
                <th key={key} className={className}>{reportYear}</th>)}

              <th className='col_target' colSpan={reportYears.length} />
            </tr>
            {reportType === 'percentage' && <tr>
              <th />
              {reportAttrs.map(attr => (
                <th
                  key={attr.key}
                  className={`col_${attr.key} main_header`}
                  colSpan={reportYears.length}
                >
                  {attr.percentLabel}
                </th>
              ))}
              <th />
            </tr>}
          </thead>
          <tbody>
            {funnelReport.map(school => (
              <tr key={school.subdomain}>
                <td className='clickable' onClick={() => onSchoolClick(school, 'admin')}>{school.name}</td>

                {mapDataColumn(({ attr, className, key, reportYear, isClickable }) =>
                  <td key={key}
                      className={classnames(className, {clickable: isClickable(school)})}
                      onClick={() => isClickable(school) && onSchoolClick(school, attr.key, reportYear)}
                  >
                    {reportType === 'count'
                      ? formatCount(school.data[attr.key][reportYear].count)
                      : formatPercent(school.data[attr.key][reportYear].percent)}
                  </td>)}

                <td className='col_target'>{school.target || '-'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Totals</td>

              {mapDataColumn(({ attr, className, key, reportYear }) =>
                <td key={key} className={className}>
                  {reportType === 'count'
                    ? aggregateCounts(attr.key, reportYear)
                    : aggregatePercents(attr.key, reportYear)}
                </td>)}

              <td className='col_target'>
                {
                  funnelReport
                    .map(school => school.target || 0)
                    .reduce((sum, val) => sum + val)
                }
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    />
  );
};

FunnelReportTable.propTypes = {
  funnelReport: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      subdomain: PropTypes.string,
      data: PropTypes.object
    })
  ).isRequired,
  reportType: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(
      PropTypes.string
    )
  }),
  asOfDate: PropTypes.string
};

export default FunnelReportTable;
